/**
 * The call controls' glyphs.
 *
 * Inline SVG with no external assets, filled/bold-stroke weight to match the
 * composer's icons rather than a thin outline set — the same rule the video
 * notes handoff set and this one repeats.
 */

interface IconProps {
  size?: number;
}

/** A filled handset. Rotated 135° by CSS to become the hang-up button. */
export function PhoneIcon({ size = 22 }: IconProps): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2z" />
    </svg>
  );
}

export function VideoIcon({ size = 22, off = false }: IconProps & { off?: boolean }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 6h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
      <path d="M18 10.5l3.2-2.3c.5-.4 1.3 0 1.3.7v6.2c0 .7-.8 1.1-1.3.7L18 13.5z" />
      {off && <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />}
    </svg>
  );
}

export function MicIcon({ size = 22, off = false }: IconProps & { off?: boolean }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path
        d="M5 11a7 7 0 0 0 14 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path d="M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8.5 21h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {off && <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />}
    </svg>
  );
}

export function SpeakerIcon({ size = 22, off = false }: IconProps & { off?: boolean }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 9h3.2L12 5.2c.6-.5 1.5 0 1.5.8v12c0 .8-.9 1.3-1.5.8L7.2 15H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z" />
      {!off && (
        <>
          <path
            d="M16.5 9.2a4 4 0 0 1 0 5.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
          <path
            d="M19 6.8a7.5 7.5 0 0 1 0 10.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
        </>
      )}
      {off && <path d="M16.5 9.5l5 5m0-5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />}
    </svg>
  );
}

/** The two-arrows-around-a-lens glyph, as the кружочек trim screen uses. */
export function FlipIcon({ size = 22 }: IconProps): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 11a8 8 0 0 0-13.3-4M4 13a8 8 0 0 0 13.3 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path d="M20 4v4h-4M4 20v-4h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="2.6" fill="currentColor" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 22 }: IconProps): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
